// Restructure a LoanDisk loan from the Customer File — CREATE-ONLY.
// Step 1 form → dry-run preview (exact POST /loan payload, blockers, warnings)
// → Step 2 type the new SF-xxxx/N to confirm → child loan created; the parent is never edited.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { previewLoanRestructure, executeLoanRestructure } from "@/lib/api";
import { fmtMoney, isAusDesk } from "@/lib/regionFmt";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const CYCLES = [{ id: 6, label: "Daily" }, { id: 4, label: "Weekly" }, { id: 9, label: "Biweekly" }, { id: 3, label: "Monthly" }];
const PRODUCTS = [
  { id: 199097, label: "FTLA, PG (Non-HO)" }, { id: 267988, label: "FTLA, PG (HO)" },
  { id: 267989, label: "FTLA, PG, Deb (Non-HO)" }, { id: 267343, label: "FTLA, PG, Deb (HO)" },
];
const todayUK = () => { const d = new Date(); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; };

function Field({ id, label, children }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export default function RestructureDialog({ open, onOpenChange, caseRow, onDone }) {
  const region = isAusDesk() ? "AUS" : "UK";
  const [form, setForm] = useState({ principal_amount: "", excess_amount: "0", interest_pct: "0", payment_scheme_id: "4",
    num_of_repayments: "", released_date: todayUK(), first_repayment_date: "", product_id: "" });
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setPv(null); setConfirm(""); };

  useEffect(() => {
    if (!open) return;
    setPv(null); setResult(null); setConfirm("");
    // principal left blank on purpose: the backend defaults it to the LATEST open link's balance (SF-xxxx/3, not the original)
    setForm((f) => ({ ...f, principal_amount: "", released_date: todayUK(), first_repayment_date: "", num_of_repayments: "" }));
  }, [open]);

  const body = () => ({
    sf_number: caseRow.loan_application_id, region, case_id: caseRow.case_id,
    principal_amount: form.principal_amount === "" ? undefined : Number(form.principal_amount),
    excess_amount: Number(form.excess_amount || 0), interest_pct: Number(form.interest_pct || 0),
    payment_scheme_id: Number(form.payment_scheme_id), num_of_repayments: form.num_of_repayments ? Number(form.num_of_repayments) : undefined,
    released_date: form.released_date || undefined, first_repayment_date: form.first_repayment_date || undefined,
    product_id: form.product_id ? Number(form.product_id) : undefined,
  });

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await previewLoanRestructure(body());
      setPv(r);
      setForm((f) => ({ ...f, principal_amount: String(r.payload.loan_principal_amount - (Number(f.excess_amount) || 0)),
        num_of_repayments: String(r.payload.loan_num_of_repayments), first_repayment_date: r.payload.loan_first_repayment_date,
        product_id: String(r.payload.loan_product_id) }));
    } catch (e) { toast.error(errorDetailMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };

  const runExecute = async () => {
    setBusy(true);
    try {
      const r = await executeLoanRestructure({ ...body(), confirm_application_id: confirm.trim() });
      setResult(r);
      toast.success(`${r.application_id} created in LoanDisk (loan ${r.child_loan_id})`);
      onDone?.(r);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Restructure failed"));
    } finally { setBusy(false); }
  };

  const canExecute = pv && !pv.blockers?.length && pv.pilot?.allowed && confirm.trim().toUpperCase() === pv.application_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="restructure-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Restructure {caseRow.loan_application_id} in LoanDisk</DialogTitle>
          <p className="text-xs text-muted-foreground">Creates a new <span className="font-mono">{pv?.application_id || `${(caseRow.loan_application_id || "").replace(/\/\d+$/, "")}/N`}</span> loan
            carrying the outstanding balance. The current loan is <strong>never edited</strong> — LoanDisk flips it to Restructured itself once the new loan is Open.</p>
        </DialogHeader>

        {result ? <ResultPanel result={result} /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field id="rs-principal" label="Balance carried"><Input id="rs-principal" data-testid="restructure-principal-input" inputMode="decimal" value={form.principal_amount} onChange={(e) => set("principal_amount", e.target.value)} placeholder="latest open link's balance (auto)" /></Field>
              <Field id="rs-excess" label="Top-up (excess)"><Input id="rs-excess" data-testid="restructure-excess-input" inputMode="decimal" value={form.excess_amount} onChange={(e) => set("excess_amount", e.target.value)} /></Field>
              <Field id="rs-interest" label="Interest %"><Input id="rs-interest" data-testid="restructure-interest-input" inputMode="decimal" value={form.interest_pct} onChange={(e) => set("interest_pct", e.target.value)} /></Field>
              <Field id="rs-cycle" label="Repayment cycle">
                <Select value={form.payment_scheme_id} onValueChange={(v) => set("payment_scheme_id", v)}>
                  <SelectTrigger id="rs-cycle" data-testid="restructure-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CYCLES.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="rs-n" label="Number of repayments"><Input id="rs-n" data-testid="restructure-repayments-input" inputMode="numeric" value={form.num_of_repayments} onChange={(e) => set("num_of_repayments", e.target.value)} placeholder="suggested on preview" /></Field>
              <Field id="rs-product" label="Product">
                <Select value={form.product_id} onValueChange={(v) => set("product_id", v)}>
                  <SelectTrigger id="rs-product" data-testid="restructure-product-select"><SelectValue placeholder="same as parent" /></SelectTrigger>
                  <SelectContent>{PRODUCTS.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="rs-released" label="Released date (dd/mm/yyyy)"><Input id="rs-released" data-testid="restructure-released-input" value={form.released_date} onChange={(e) => set("released_date", e.target.value)} /></Field>
              <Field id="rs-first" label="First repayment (dd/mm/yyyy)"><Input id="rs-first" data-testid="restructure-first-repayment-input" value={form.first_repayment_date} onChange={(e) => set("first_repayment_date", e.target.value)} placeholder="auto from cycle" /></Field>
              <div className="flex items-end"><Button size="sm" className="w-full" onClick={runPreview} disabled={busy} data-testid="restructure-preview-btn">{busy && !pv ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Preview (dry run)</Button></div>
            </div>

            {pv && <PreviewPanel pv={pv} showJson={showJson} setShowJson={setShowJson} />}

            {pv && !pv.blockers?.length && (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="restructure-confirm-panel">
                {!pv.pilot?.allowed && (
                  <p className="text-xs text-amber-900 flex items-start gap-1.5" data-testid="restructure-pilot-blocked"><ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Pilot mode — <span className="font-mono mx-1">{pv.base_sf}</span> is not on the allow-list yet. An Admin adds it under Portal Settings (PUT /loandisk/restructure/settings) after the first test loan is verified.</p>
                )}
                <Label htmlFor="rs-confirm" className="text-xs">Type <span className="font-mono font-semibold">{pv.application_id}</span> to confirm the new loan</Label>
                <Input id="rs-confirm" data-testid="restructure-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={pv.application_id} className="font-mono" />
              </div>
            )}
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="restructure-close-btn">{result ? "Close" : "Cancel"}</Button>
          {!result && <Button size="sm" onClick={runExecute} disabled={!canExecute || busy} className="bg-rose-600 hover:bg-rose-700" data-testid="restructure-execute-btn">
            {busy && pv ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create {pv?.application_id || "new loan"} in LoanDisk</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewPanel({ pv, showJson, setShowJson }) {
  const p = pv.parent || {};
  return (
    <div className="mt-3 space-y-2 text-xs" data-testid="restructure-preview">
      <div className="rounded-md border p-2 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Parent" value={<span className="font-mono">{p.loan_application_id}</span>} sub={`${p.status || ""} · ${p.product || ""}`} />
        <Stat label="Parent balance" value={fmtMoney(p.balance ?? 0, 2)} sub={`P ${fmtMoney(p.principal_balance ?? 0, 2)} · I ${fmtMoney(p.interest_balance ?? 0, 2)} · F ${fmtMoney(p.fees_balance ?? 0, 2)}`} />
        <Stat label="New loan" value={<span className="font-mono">{pv.application_id}</span>} sub={`${fmtMoney(pv.payload.loan_principal_amount, 2)} @ ${pv.payload.loan_interest}%`} />
        <Stat label="Schedule" value={`${pv.payload.loan_num_of_repayments} × ${pv.cycle}`} sub={`≈ ${fmtMoney(pv.instalment, 2)} each · first ${pv.payload.loan_first_repayment_date}`} />
      </div>
      {pv.family?.length > 1 && <p className="text-muted-foreground">Family: {pv.family.map((f) => `${f.loan_application_id} (${f.status})`).join(" → ")}</p>}
      {pv.suggestion?.parent_instalment != null && <p className="text-muted-foreground">Parent was paying {fmtMoney(pv.suggestion.parent_instalment, 2)} {pv.suggestion.parent_cycle}; suggested {pv.suggestion.suggested_num_of_repayments} repayments keeps the instalment near {fmtMoney(pv.suggestion.suggested_instalment ?? 0, 2)}.</p>}
      {pv.blockers?.map((b) => <p key={b} className="text-rose-700 flex gap-1.5" data-testid="restructure-blocker"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{b}</p>)}
      {pv.warnings?.map((w) => <p key={w} className="text-amber-700 flex gap-1.5" data-testid="restructure-warning"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}</p>)}
      <button type="button" className="underline text-muted-foreground" onClick={() => setShowJson(!showJson)} data-testid="restructure-toggle-json">{showJson ? "Hide" : "Show"} exact POST /loan payload</button>
      {showJson && <pre className="rounded bg-slate-900 text-slate-100 p-2 overflow-x-auto text-[10px]" data-testid="restructure-payload-json">{JSON.stringify(pv.payload, null, 2)}</pre>}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-semibold truncate">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={typeof sub === "string" ? sub : undefined}>{sub}</div>}
    </div>
  );
}

function ResultPanel({ result }) {
  return (
    <div className="space-y-2 text-xs" data-testid="restructure-result">
      <p className="flex items-center gap-1.5 text-emerald-700 font-semibold"><CheckCircle2 className="w-4 h-4" /> {result.application_id} created — LoanDisk loan {result.child_loan_id}</p>
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className={result.parent_flipped ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-800"} data-testid="restructure-parent-flag">
          Parent {result.parent_flipped ? "flipped to Restructured" : `status ${result.parent_status_after ?? "?"} — not flipped yet`}
        </Badge>
        <Badge variant="outline" className={result.child_linked_to_parent ? "border-emerald-300 text-emerald-700" : "border-slate-300"}>{result.child_linked_to_parent ? "Linked to parent" : "No parent link recorded"}</Badge>
      </div>
      {result.note && <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{result.note}</p>}
      <a className="inline-flex items-center gap-1 underline" href={`https://app.loandisk.com/loan/view/${result.child_loan_id}`} target="_blank" rel="noreferrer" data-testid="restructure-open-child">Open {result.application_id} in LoanDisk <ExternalLink className="w-3 h-3" /></a>
    </div>
  );
}
